import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY, formatDate, todayISO } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, Loader2, Link2, Trash2, Sparkles, BookMarked } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", payment_date: todayISO(), amount: 0, fee: 0, payer_name: "", memo: "" });
  const [matchFor, setMatchFor] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [busy, setBusy] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [detectedCustomer, setDetectedCustomer] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSel, setBulkSel] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [aliasOpen, setAliasOpen] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [aliasForm, setAliasForm] = useState({ payer_name: "", customer_id: "" });
  const aliasFileRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/billing/payments").then((r) => setPayments(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const addPayment = async () => {
    if (!form.amount) { toast.error("金額を入力してください"); return; }
    try {
      await api.post("/billing/payments", { ...form, amount: Number(form.amount), fee: Number(form.fee) });
      toast.success("入金を登録しました"); setAddOpen(false);
      setForm({ customer_id: "", payment_date: todayISO(), amount: 0, fee: 0, payer_name: "", memo: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/billing/payments/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rows = r.data.rows || [];
      if (!rows.length) { toast.info("入金明細が見つかりませんでした"); return; }
      let n = 0;
      for (const row of rows) {
        await api.post("/billing/payments", { payment_date: row.date, amount: row.amount, payer_name: row.description, memo: "銀行CSV取込" });
        n++;
      }
      toast.success(`${n} 件の入金を取り込みました`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const openMatch = async (p) => {
    setMatchFor(p); setCandidates([]); setAlloc({}); setDetectedCustomer(null);
    try {
      const r = await api.get(`/billing/payments/${p.id}/match`);
      setCandidates(r.data.candidates || []);
      // preselect best full match
      const best = (r.data.candidates || [])[0];
      if (best && best.confidence >= 0.6) setAlloc({ [best.invoice_id]: best.outstanding });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runAiMatch = async () => {
    if (!matchFor) return;
    setAiLoading(true);
    try {
      const r = await api.post(`/billing/payments/${matchFor.id}/ai-match`);
      setCandidates(r.data.candidates || []);
      setDetectedCustomer(r.data.detected_customer || null);
      const best = (r.data.candidates || [])[0];
      if (best && best.confidence >= 0.6) setAlloc({ [best.invoice_id]: best.outstanding });
      if (!r.data.ai_used) toast.info("AI照合は利用できないため、通常照合の結果を表示しています");
      else toast.success("AI照合が完了しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const confirmAlloc = async () => {
    const allocations = Object.entries(alloc).filter(([, v]) => Number(v) > 0).map(([invoice_id, amount]) => ({ invoice_id, amount: Number(amount) }));
    if (!allocations.length) { toast.error("消込先を選択してください"); return; }
    setBusy(true);
    try {
      await api.post(`/billing/payments/${matchFor.id}/allocate`, { allocations, fee: Number(matchFor.fee || 0) });
      toast.success("消込を確定し、入金仕訳候補を作成しました");
      setMatchFor(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm("この入金を削除しますか？消込も解除されます。")) return;
    try { await api.delete(`/billing/payments/${p.id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runBulkAiMatch = async () => {
    setBulkLoading(true); setBulkOpen(true); setBulkRows([]); setBulkSel({});
    try {
      const r = await api.post(`/billing/payments/bulk-ai-match?threshold=0.8`);
      const rows = r.data.results || [];
      setBulkRows(rows);
      // 高確信(auto)は事前選択（消込予約）。残高全額充当し、差額(0〜2000円)は振込手数料として自動計上
      const sel = {};
      rows.forEach((row) => {
        if (row.auto && row.best) {
          const diff = Math.round(row.best.outstanding - row.amount);
          sel[row.payment_id] = { invoice_id: row.best.invoice_id, amount: row.best.outstanding, fee: diff > 0 && diff <= 2000 ? diff : 0 };
        }
      });
      setBulkSel(sel);
      if (r.data.auto_count > 0) toast.success(`${r.data.auto_count}件を高確信として消込予約しました`);
      else toast.info("高確信の自動予約はありません。候補を確認してください");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkLoading(false); }
  };

  const toggleBulk = (row) => {
    setBulkSel((s) => {
      const n = { ...s };
      if (n[row.payment_id]) delete n[row.payment_id];
      else if (row.best) { const diff = Math.round(row.best.outstanding - row.amount); n[row.payment_id] = { invoice_id: row.best.invoice_id, amount: row.best.outstanding, fee: diff > 0 && diff <= 2000 ? diff : 0 }; }
      return n;
    });
  };

  const confirmBulk = async () => {
    const items = Object.entries(bulkSel).map(([payment_id, v]) => ({ payment_id, invoice_id: v.invoice_id, amount: v.amount, fee: v.fee || 0 }));
    if (!items.length) { toast.error("消込する入金を選択してください"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post(`/billing/payments/bulk-allocate`, { items });
      toast.success(`${r.data.allocated}件を一括消込しました${r.data.skipped ? `（${r.data.skipped}件スキップ）` : ""}`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const openAliases = async () => {
    setAliasOpen(true);
    try { const r = await api.get(`/billing/reconcile-aliases`); setAliases(r.data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteAlias = async (id) => {
    try { await api.delete(`/billing/reconcile-aliases/${id}`); setAliases((a) => a.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addAlias = async () => {
    if (!aliasForm.payer_name.trim() || !aliasForm.customer_id) { toast.error("振込名義と取引先を入力してください"); return; }
    try {
      await api.post(`/billing/reconcile-aliases`, aliasForm);
      setAliasForm({ payer_name: "", customer_id: "" });
      const r = await api.get(`/billing/reconcile-aliases`); setAliases(r.data || []);
      toast.success("辞書に登録しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const importAliases = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/billing/reconcile-aliases/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rr = await api.get(`/billing/reconcile-aliases`); setAliases(rr.data || []);
      toast.success(`${r.data.imported}件を取込みました${r.data.unmatched_count ? `（未一致${r.data.unmatched_count}件）` : ""}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (aliasFileRef.current) aliasFileRef.current.value = ""; }
  };

  const exportAliases = async () => {
    try {
      const r = await api.get(`/billing/reconcile-aliases/export/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "reconcile_aliases.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const statusLabel = { unallocated: "未消込", allocated: "消込済み" };

  return (
    <div data-testid="billing-payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-stone-900">入金・消込</h1>
          <p className="text-sm text-stone-500 mt-0.5">{payments.length} 件</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="bank-csv-input" />
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50" data-testid="import-csv-button">
            <Upload className="h-4 w-4" /> 銀行CSV取込
          </button>
          <button onClick={runBulkAiMatch} className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-sm font-medium text-white hover:opacity-90" data-testid="bulk-ai-match-button">
            <Sparkles className="h-4 w-4" /> AI一括照合
          </button>
          <button onClick={openAliases} className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50" data-testid="aliases-button">
            <BookMarked className="h-4 w-4" /> 学習辞書
          </button>
          <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700" data-testid="add-payment-button">
            <Plus className="h-4 w-4" /> 入金登録
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">入金日</th>
              <th className="px-4 py-3 text-left">振込名義 / 取引先</th>
              <th className="px-4 py-3 text-right">金額</th>
              <th className="px-4 py-3 text-right">手数料</th>
              <th className="px-4 py-3 text-left">状態</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400">入金はまだありません</td></tr>
            ) : payments.map((p) => (
              <tr key={p.id} className="hover:bg-stone-50/60" data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-3 text-stone-700">{formatDate(p.payment_date)}</td>
                <td className="px-4 py-3 text-stone-700">{p.payer_name || p.customer_name || "—"}</td>
                <td className="px-4 py-3 text-right font-medium">{formatJPY(p.amount)}</td>
                <td className="px-4 py-3 text-right text-stone-500">{formatJPY(p.fee)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${p.status === "allocated" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{statusLabel[p.status] || p.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {p.status !== "allocated" && (
                      <button onClick={() => openMatch(p)} className="inline-flex items-center gap-1 text-indigo-600 text-xs font-medium hover:text-indigo-800" data-testid={`match-button-${p.id}`}>
                        <Link2 className="h-3.5 w-3.5" /> 消込
                      </button>
                    )}
                    <button onClick={() => del(p)} className="text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add payment */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-payment-dialog">
          <DialogHeader><DialogTitle>入金登録</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>入金日</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></div>
              <div>
                <Label>取引先（任意）</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger><SelectValue placeholder="選択" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>振込名義</Label><Input value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} data-testid="payment-payer" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>入金額</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" /></div>
              <div><Label>振込手数料控除</Label><Input type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} data-testid="payment-fee" /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAddOpen(false)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={addPayment} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700" data-testid="payment-save-button">登録</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Match / allocate */}
      <Dialog open={!!matchFor} onOpenChange={(o) => !o && setMatchFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="match-dialog">
          <DialogHeader><DialogTitle>入金消込 — {matchFor && formatJPY(matchFor.amount)}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-stone-500 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-indigo-500" /> 自動照合結果（確信度・根拠を確認して消込を確定してください）</p>
            <button onClick={runAiMatch} disabled={aiLoading} className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60" data-testid="ai-match-button">
              {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AIで照合
            </button>
          </div>
          {detectedCustomer && (
            <div className="rounded-md bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-700" data-testid="detected-customer">
              AI推定取引先: <span className="font-semibold">{detectedCustomer}</span>
            </div>
          )}
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {candidates.length === 0 ? (
              <div className="py-8 text-center text-sm text-stone-400">候補となる請求書がありません</div>
            ) : candidates.map((c) => (
              <label key={c.invoice_id} className="flex items-center gap-3 rounded-lg border border-stone-200 p-3 cursor-pointer hover:border-indigo-300" data-testid={`candidate-${c.invoice_id}`}>
                <input type="checkbox" checked={!!alloc[c.invoice_id]} onChange={(e) => setAlloc((a) => { const n = { ...a }; if (e.target.checked) n[c.invoice_id] = c.outstanding; else delete n[c.invoice_id]; return n; })} className="h-4 w-4" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-stone-900">{c.doc_number}</span>
                    <span className="rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[11px]">確信度 {Math.round(c.confidence * 100)}%</span>
                    {c.ai_confidence != null && <span className="rounded-full bg-violet-50 text-violet-700 px-2 py-0.5 text-[11px] flex items-center gap-0.5"><Sparkles className="h-2.5 w-2.5" />AI {Math.round(c.ai_confidence * 100)}%</span>}
                  </div>
                  <div className="text-xs text-stone-500">{c.customer_name} ・ 残高 {formatJPY(c.outstanding)}</div>
                  <div className="text-[11px] text-stone-400">{c.reasons.join(" / ")}</div>
                </div>
                {alloc[c.invoice_id] !== undefined && (
                  <Input type="number" value={alloc[c.invoice_id]} onChange={(e) => setAlloc((a) => ({ ...a, [c.invoice_id]: e.target.value }))} className="w-28 h-8 text-sm" onClick={(e) => e.preventDefault()} />
                )}
              </label>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setMatchFor(null)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={confirmAlloc} disabled={busy} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="allocate-confirm-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} 消込を確定
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk AI match / reserve */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-indigo-600" /> AI一括照合・消込予約</DialogTitle></DialogHeader>
          <p className="text-xs text-stone-500">未消込の全入金をAIで照合しました。高確信の入金はチェック済み（消込予約）です。内容を確認して一括消込してください。</p>
          {bulkLoading ? (
            <div className="py-16 text-center text-stone-400"><Loader2 className="h-6 w-6 animate-spin inline" /> 照合中…</div>
          ) : bulkRows.length === 0 ? (
            <div className="py-12 text-center text-sm text-stone-400">未消込の入金がありません</div>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {bulkRows.map((row) => {
                const sel = bulkSel[row.payment_id];
                return (
                  <div key={row.payment_id} className={`rounded-lg border p-3 ${sel ? "border-indigo-300 bg-indigo-50/40" : "border-stone-200"}`} data-testid={`bulk-row-${row.payment_id}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={!!sel} disabled={!row.best} onChange={() => toggleBulk(row)} className="h-4 w-4" data-testid={`bulk-check-${row.payment_id}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-stone-900">{row.payer_name || "（振込名義なし）"}</span>
                          <span className="text-stone-500 text-sm">{formatJPY(row.amount)}</span>
                          {row.auto && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px]">高確信・予約済</span>}
                          {row.learned_used && <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[11px] flex items-center gap-0.5"><BookMarked className="h-2.5 w-2.5" />学習済み</span>}
                        </div>
                        {row.best ? (
                          <div className="text-xs text-stone-500 mt-0.5">
                            → {row.best.doc_number}（{row.best.customer_name}・残高 {formatJPY(row.best.outstanding)}）
                            <span className="ml-1 rounded-full bg-indigo-50 text-indigo-700 px-1.5 py-0.5">確信度 {Math.round(row.best.confidence * 100)}%</span>
                            {row.best.reasons?.length ? <span className="text-[11px] text-stone-400 ml-1">{row.best.reasons.join(" / ")}</span> : null}
                          </div>
                        ) : (
                          <div className="text-xs text-amber-600 mt-0.5">候補なし（手動で消込してください）</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <span className="mr-auto text-xs text-stone-500 self-center">{Object.keys(bulkSel).length} 件を消込予約中</span>
            <button onClick={() => setBulkOpen(false)} className="px-4 py-2 text-sm text-stone-600">閉じる</button>
            <button onClick={confirmBulk} disabled={bulkBusy || Object.keys(bulkSel).length === 0} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="bulk-confirm-button">
              {bulkBusy && <Loader2 className="h-4 w-4 animate-spin" />} 選択を一括消込
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 照合学習辞書 */}
      <Dialog open={aliasOpen} onOpenChange={setAliasOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="aliases-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookMarked className="h-5 w-5 text-amber-600" /> 照合学習辞書</DialogTitle></DialogHeader>
          <p className="text-xs text-stone-500">消込を確定すると「振込名義 → 取引先」を自動学習します。次回以降は同じ振込名義をAIなしで即マッチします。よく使う取引先は事前登録・CSVインポートも可能です。</p>

          <div className="flex items-center gap-2 flex-wrap">
            <input ref={aliasFileRef} type="file" accept=".csv" className="hidden" onChange={importAliases} data-testid="alias-import-input" />
            <button onClick={() => aliasFileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50" data-testid="alias-import-button"><Upload className="h-3.5 w-3.5" /> CSVインポート</button>
            <button onClick={exportAliases} className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50" data-testid="alias-export-button"><Upload className="h-3.5 w-3.5 rotate-180" /> CSV出力</button>
            <span className="text-[11px] text-stone-400">CSV形式: 1列目=振込名義, 2列目=取引先名（既存の取引先名で名寄せ）</span>
          </div>

          <div className="rounded-lg border border-stone-200 p-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">振込名義</Label>
              <Input value={aliasForm.payer_name} onChange={(e) => setAliasForm({ ...aliasForm, payer_name: e.target.value })} placeholder="例: ｶ)エービーシー" data-testid="alias-payer-input" />
            </div>
            <div>
              <Label className="text-xs">取引先</Label>
              <Select value={aliasForm.customer_id} onValueChange={(v) => setAliasForm({ ...aliasForm, customer_id: v })}>
                <SelectTrigger data-testid="alias-customer-select"><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button onClick={addAlias} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-600 text-white px-3 py-2 text-sm font-medium hover:bg-amber-700" data-testid="alias-add-button"><Plus className="h-4 w-4" /> 登録</button>
          </div>

          {aliases.length === 0 ? (
            <div className="py-12 text-center text-sm text-stone-400">まだ学習された振込名義はありません</div>
          ) : (
            <div className="divide-y divide-stone-100">
              {aliases.map((a) => (
                <div key={a.id} className="flex items-center gap-3 py-2.5" data-testid={`alias-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800 truncate">{a.payer_sample || a.payer_key}</div>
                    <div className="text-xs text-stone-500 truncate">→ {a.customer_name}　<span className="text-stone-400">利用{a.hit_count || 1}回</span></div>
                  </div>
                  <button onClick={() => deleteAlias(a.id)} className="p-1.5 text-stone-400 hover:text-red-600" data-testid={`alias-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAliasOpen(false)} className="px-4 py-2 text-sm text-stone-600">閉じる</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
